"use client";

import type { PropsWithChildren } from "react";
import { useMemo, useState } from "react";
import { cookieService } from "@/app";
import { MBOXES, type MBoxKeys } from "./mboxes-config";
import {
  type TargeApiProviderContextType,
  TargetApiProviderContext,
} from "./target-api-provider.context";
import type {
  ActivatedTestsType,
  AdobeScript,
  GetOffersResponse,
} from "./target-api-provider.type";

export interface TargetApiProviderProps extends PropsWithChildren {}

export function TargetApiProvider({
  children,
}: Readonly<TargetApiProviderProps>) {
  const [activatedTests, setActivatedTests] = useState<ActivatedTestsType>({});

  const saveOnCache = (localStorageKey: string, currContent: string) => {
    cookieService.save(localStorageKey, currContent, { expires: 1 });
  };

  const getAllFromCache = (
    mboxEntries: [string, { contentKey: string }][]
  ) => {
    const activatedLocalStorage: ActivatedTestsType = {};
    mboxEntries.forEach(([_mboxName, { contentKey }]) => {
      const mboxCache = cookieService.get<string>(
        `${process.env.NEXT_PUBLIC_ENVIRONMENT}_${contentKey}`
      );

      if (mboxCache) activatedLocalStorage[contentKey] = mboxCache;
    });
    return activatedLocalStorage;
  };

  const handleTargetApiReponse = (
    targetApiData: GetOffersResponse,
    activatedLocalStorage: ActivatedTestsType
  ) => {
    const activatedAdobeAPI: ActivatedTestsType = {};

    targetApiData.execute?.mboxes?.forEach((mboxesResponse) => {
      const mbox = MBOXES[mboxesResponse.name];
      const cacheKey = `${process.env.NEXT_PUBLIC_ENVIRONMENT}_${mbox?.contentKey}`;

      if (mboxesResponse.options) {
        mboxesResponse.options.forEach((item) => {
          if (mbox && item.type == "json" && item.content[mbox.contentKey]) {
            const currContent = item.content[mbox.contentKey];

            activatedAdobeAPI[mbox.contentKey] = item.content[mbox.contentKey];
            saveOnCache(cacheKey, currContent);
          }
        });
      }
    });
    if (Object.values(activatedAdobeAPI).length > 0) {
      setActivatedTests({ ...activatedLocalStorage, ...activatedAdobeAPI });
    }
  };

  const sendTargetAPI = (adobe: AdobeScript) => {
    const mboxEntries = Object.entries(MBOXES);

    const activatedLocalStorage = getAllFromCache(
      mboxEntries as [string, { contentKey: string }][]
    );
    setActivatedTests({ ...activatedLocalStorage });

    const mboxesPayload = mboxEntries.map((mbox, index) => {
      return { index, name: mbox[0] as MBoxKeys };
    });

    adobe?.target
      ?.getOffers({
        request: {
          execute: {
            mboxes: mboxesPayload,
          },
        },
      })
      .then((targetApiData) =>
        handleTargetApiReponse(targetApiData, activatedLocalStorage)
      );
  };

  const value = useMemo<TargeApiProviderContextType>(
    () => ({ activatedTests, sendTargetAPI }),
    [activatedTests]
  );

  return (
    <TargetApiProviderContext.Provider value={value}>
      {children}
    </TargetApiProviderContext.Provider>
  );
}
